import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { describeGoogleSignInError } from '../utils/googleSignInErrors';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Email,
  Lock,
  Business,
  Security,
  ArrowForward
} from '@mui/icons-material';

// Dark theme colors matching marketing pages
const colors = {
  bgPrimary: '#0a0a0a',
  bgSecondary: '#141414',
  bgSurface: '#1a1a1a',
  border: '#2a2a2a',
  textPrimary: '#e5e5e5',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  accentPrimary: '#3b82f6',
  accentSecondary: '#8b5cf6',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b'
};

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: colors.bgPrimary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px'
});

const FormCard = styled(Box)({
  backgroundColor: colors.bgSurface,
  border: `1px solid ${colors.border}`,
  borderRadius: '16px',
  padding: '40px',
  maxWidth: '420px',
  width: '100%'
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.bgSecondary,
    color: colors.textPrimary,
    borderRadius: '8px',
    '& fieldset': {
      borderColor: colors.border,
    },
    '&:hover fieldset': {
      borderColor: colors.accentPrimary,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.accentPrimary,
    },
  },
  '& .MuiInputLabel-root': {
    color: colors.textSecondary,
    '&.Mui-focused': {
      color: colors.accentPrimary,
    },
  },
  '& .MuiInputAdornment-root': {
    color: colors.textMuted,
  },
});

const GradientButton = styled(Button)({
  background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '1rem',
  '&:hover': {
    background: `linear-gradient(135deg, ${colors.accentPrimary} 20%, ${colors.accentSecondary} 100%)`,
    boxShadow: `0 4px 20px rgba(59, 130, 246, 0.4)`,
  },
  '&:disabled': {
    background: colors.border,
    color: colors.textMuted,
  },
});

const OutlinedButton = styled(Button)({
  color: colors.textPrimary,
  borderColor: colors.border,
  textTransform: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '1rem',
  '&:hover': {
    borderColor: colors.accentPrimary,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
  },
});

const SSOBox = styled(Box)({
  padding: '16px',
  backgroundColor: colors.bgSecondary,
  border: `1px solid ${colors.accentPrimary}`,
  borderRadius: '12px',
  marginBottom: '16px'
});

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [ssoInfo, setSsoInfo] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  // Set when the server refuses login because the email isn't verified yet,
  // which surfaces a "resend verification email" action.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resending, setResending] = useState(false);
  const debounceTimeout = useRef(null);

  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  // A Google sign-in that fails part way lands back here with a code, since
  // there is no callback page to show it on yet.
  const googleError = describeGoogleSignInError(searchParams.get('error'));

  const getRedirectPath = () => {
    if (redirectUrl && redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')) {
      return redirectUrl;
    }
    return '/dashboard';
  };

  useEffect(() => {
    if (googleError) setError(googleError);
  }, [googleError]);

  // SSO Detection
  const detectSSO = async (emailValue) => {
    if (!emailValue || !emailValue.includes('@') || !emailValue.includes('.') || emailValue.endsWith('@')) {
      setSsoInfo(null);
      return;
    }

    setSsoLoading(true);
    try {
      const response = await api.post('/sso/detect', { email: emailValue });
      if (response.data.sso) {
        setSsoInfo(response.data.sso);
      } else {
        setSsoInfo(null);
      }
    } catch (error) {
      console.error('Error detecting SSO:', error);
      setSsoInfo(null);
    } finally {
      setSsoLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setError('');

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      detectSSO(newEmail);
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleSSOLogin = () => {
    if (ssoInfo) {
      window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/sso/login/${ssoInfo.organizationId}`;
    }
  };

  const handleContinueWithPassword = () => {
    setShowPasswordForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendMessage('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(getRedirectPath());
    } catch (err) {
      // A 403 with needsVerification means the credentials were right but the
      // email is still unconfirmed; offer a resend rather than a generic error.
      if (err.response?.status === 403 && err.response?.data?.needsVerification) {
        setNeedsVerification(true);
        setError(err.response.data.message || 'Please verify your email address before logging in.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to login. Please check your credentials.');
      }
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendMessage('');
    setResending(true);
    try {
      await resendVerification(email);
      setResendMessage('If your account still needs verifying, a new link is on its way.');
    } catch (err) {
      setResendMessage('Could not resend right now. Please try again shortly.');
    }
    setResending(false);
  };

  return (
    <PageWrapper>
      <Container maxWidth="sm">
        <FormCard>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, ${colors.accentSecondary} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}
            >
              <LoginIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                color: colors.textPrimary,
                fontWeight: 700,
                mb: 1
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.textSecondary }}
            >
              Sign in to your account
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: colors.error,
                border: `1px solid ${colors.error}`,
                '& .MuiAlert-icon': { color: colors.error }
              }}
            >
              {error}
            </Alert>
          )}

          {needsVerification && (
            <Box sx={{ mb: 3 }}>
              <GradientButton
                fullWidth
                onClick={handleResendVerification}
                disabled={resending}
              >
                {resending ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Resend verification email'}
              </GradientButton>
              {resendMessage && (
                <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: 'center', mt: 1.5 }}>
                  {resendMessage}
                </Typography>
              )}
            </Box>
          )}

          {!showPasswordForm ? (
            // Email + SSO detection step
            <Box>
              <StyledTextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                disabled={ssoLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {ssoLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" my={2}>
                  <CircularProgress size={20} sx={{ color: colors.accentPrimary }} />
                  <Typography variant="body2" sx={{ ml: 1, color: colors.textSecondary }}>
                    Checking for SSO...
                  </Typography>
                </Box>
              )}

              {ssoInfo && (
                <SSOBox>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Business sx={{ color: colors.accentPrimary, mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                      {ssoInfo.organizationName}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
                    This organization uses Single Sign-On (SSO) for secure authentication.
                  </Typography>

                  {ssoInfo.forceSso && (
                    <Chip
                      icon={<Security sx={{ color: colors.warning }} />}
                      label="SSO Required"
                      size="small"
                      sx={{
                        mb: 2,
                        backgroundColor: 'rgba(245, 158, 11, 0.2)',
                        color: colors.warning,
                        border: `1px solid ${colors.warning}`
                      }}
                    />
                  )}

                  <GradientButton
                    fullWidth
                    onClick={handleSSOLogin}
                    startIcon={<Security />}
                    endIcon={<ArrowForward />}
                  >
                    Continue with {ssoInfo.organizationName} SSO
                  </GradientButton>

                  {!ssoInfo.forceSso && (
                    <>
                      <Divider sx={{ my: 2, borderColor: colors.border }}>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          OR
                        </Typography>
                      </Divider>

                      <OutlinedButton
                        fullWidth
                        variant="outlined"
                        onClick={handleContinueWithPassword}
                        disabled={!email}
                      >
                        Continue with Password
                      </OutlinedButton>
                    </>
                  )}
                </SSOBox>
              )}

              {!ssoInfo && email && !ssoLoading && (
                <GradientButton
                  fullWidth
                  onClick={handleContinueWithPassword}
                  disabled={!email}
                  sx={{ mt: 2 }}
                >
                  Continue
                </GradientButton>
              )}

              {!email && (
                <Typography variant="body2" sx={{ color: colors.textMuted, textAlign: 'center', mt: 2 }}>
                  Enter your email to get started
                </Typography>
              )}

              <Divider sx={{ my: 2.5, borderColor: colors.border }}>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  OR
                </Typography>
              </Divider>

              <GoogleSignInButton redirectPath={getRedirectPath()} />
            </Box>
          ) : (
            // Password step
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <StyledTextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: colors.textMuted }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <GradientButton
                type="submit"
                fullWidth
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
              </GradientButton>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  variant="text"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPassword('');
                    setSsoInfo(null);
                  }}
                  size="small"
                  sx={{ color: colors.textSecondary, textTransform: 'none' }}
                >
                  ← Back
                </Button>

                <Link
                  component={RouterLink}
                  to={redirectUrl ? `/register?redirect=${encodeURIComponent(redirectUrl)}` : '/register'}
                  sx={{
                    color: colors.accentPrimary,
                    fontWeight: 600,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Create account
                </Link>
              </Box>
            </Box>
          )}

          {!showPasswordForm && (
            <Box textAlign="center" mt={3}>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Don't have an account?{' '}
                <Link
                  component={RouterLink}
                  to={redirectUrl ? `/register?redirect=${encodeURIComponent(redirectUrl)}` : '/register'}
                  sx={{
                    color: colors.accentPrimary,
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
          )}
        </FormCard>
      </Container>
    </PageWrapper>
  );
};

export default LoginPage;
